'use client';
import { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Link from '@/components/ui/Link';

interface TeamMember {
    name: string;
    role: string;
    image: string;
    alt: string;
    quote: string;
}

interface TimelineEvent {
    year: string;
    title: string;
    description: string;
}

const teamMembers: TeamMember[] = [
    {
        name: 'Arjun Mehta',
        role: 'Founder & Creative Director',
        image: '/images/team_arjun.webp',
        alt: 'Arjun Mehta, Founder and Creative Director of NEHDO Wellness',
        quote: 'Fashion should feel like a second skin — effortless, intentional, yours.',
    },
    {
        name: 'Priya Nair',
        role: 'Head of Sustainability',
        image: '/images/team_priya.webp',
        alt: 'Priya Nair, Head of Sustainability at NEHDO Wellness',
        quote: 'Every stitch we make is a promise to the planet we share.',
    },
    {
        name: 'Rohan Desai',
        role: 'Chief Product Officer',
        image: '/images/team_rohan.webp',
        alt: 'Rohan Desai, Chief Product Officer at NEHDO Wellness',
        quote: 'We obsess over details so our customers never have to.',
    },
    {
        name: 'Sneha Kapoor',
        role: 'Brand & Marketing Lead',
        image: '/images/team_sneha.webp',
        alt: 'Sneha Kapoor, Brand and Marketing Lead at NEHDO Wellness',
        quote: 'Authentic style isn\'t bought — it\'s built, one choice at a time.',
    },
];

const timeline: TimelineEvent[] = [
    {
        year: '2016',
        title: 'The First Stitch',
        description: 'NEHDO Wellness was born in a small Surat workshop with a single belief: quality clothing should be accessible to everyone, not just the privileged few.',
    },
    {
        year: '2018',
        title: 'Going Organic',
        description: 'We transitioned 60% of our supply chain to certified organic cotton, partnering with 12 family-owned farms across Gujarat and Maharashtra.',
    },
    {
        year: '2020',
        title: 'Digital First',
        description: 'Launched our online flagship store, reaching customers across 28 Indian states. Our first collection sold out in 72 hours.',
    },
    {
        year: '2022',
        title: 'Carbon Neutral Pledge',
        description: 'Achieved carbon-neutral shipping across all domestic orders. Planted 50,000 trees in partnership with local reforestation NGOs.',
    },
    {
        year: '2024',
        title: 'Global Reach',
        description: 'Expanded to 14 countries. Opened our first physical experience store in Mumbai, blending retail with community and wellness.',
    },
    {
        year: '2026',
        title: 'Full Circle',
        description: 'Launched our take-back programme — customers return worn garments for recycling or upcycling, closing the loop on fashion waste.',
    },
];

const sustainabilityPillars = [
    {
        icon: '🌿',
        title: 'Organic Materials',
        stat: '78%',
        description: 'of our fabrics are certified organic or recycled, sourced from verified ethical suppliers.',
    },
    {
        icon: '💧',
        title: 'Water Saved',
        stat: '2.4M L',
        description: 'saved annually through our low-impact dyeing processes and closed-loop water systems.',
    },
    {
        icon: '♻️',
        title: 'Zero Waste Goal',
        stat: '2028',
        description: 'our target year to achieve zero textile waste across all production facilities.',
    },
    {
        icon: '🤝',
        title: 'Fair Wages',
        stat: '100%',
        description: 'of our artisans and factory workers earn above living wage — verified by third-party audits.',
    },
];

export default function AboutPage() {
    const [activeYear, setActiveYear] = useState('2016');

    const activeEvent = timeline.find(e => e.year === activeYear) || timeline[0];

    return (
        <>
            <Header />
            <main className="w-full bg-background-tertiary min-h-screen">

                {/* ── Hero Banner ── */}
                <section className="relative w-full overflow-hidden bg-[#1f1915]">
                    <div
                        className="absolute inset-0 opacity-45"
                        style={{
                            backgroundImage: `url('/images/img_rectangle_3.png')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center top',
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />

                    <div className="relative w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] py-16 sm:py-24 lg:py-32">
                        <div className="max-w-2xl">
                            <span className="inline-block text-primary-accent text-xs sm:text-sm font-bold uppercase tracking-[4px] mb-3">
                                Our Story
                            </span>
                            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                                Dressed in<br />
                                <span className="text-primary-accent">Purpose.</span>
                            </h1>
                            <p className="text-white/85 text-base sm:text-lg lg:text-xl leading-relaxed max-w-xl">
                                NEHDO Wellness started with a question: why should fashion cost the earth? A decade later, we're still answering it — one garment at a time.
                            </p>
                            <div className="flex flex-wrap gap-4 mt-8">
                                <Link
                                    href="/shop"
                                    ariaLabel="Shop our collection"
                                    className="inline-flex items-center gap-2 bg-primary-brand text-white font-semibold px-6 py-3.5 rounded-full hover:bg-primary-brand-dark transition-colors duration-200 text-sm sm:text-base shadow-sm"
                                >
                                    Shop the Collection →
                                </Link>
                                <a
                                    href="#our-mission"
                                    className="inline-flex items-center gap-2 border border-white/60 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white hover:text-text-primary transition-all duration-200 text-sm sm:text-base"
                                >
                                    Learn More
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Floating stat cards */}
                    <div className="relative w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] pb-10 lg:pb-16">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                            {[
                                { value: '10+', label: 'Years of craft' },
                                { value: '14', label: 'Countries served' },
                                { value: '50K+', label: 'Trees planted' },
                                { value: '200K+', label: 'Happy customers' },
                            ].map((stat) => (
                                <div
                                    key={stat.label}
                                    className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 sm:p-5 text-center shadow-lg"
                                >
                                    <p className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</p>
                                    <p className="text-white/80 text-xs sm:text-sm mt-1 font-medium">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Brand Story + Mission ── */}
                <section id="our-mission" className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] py-16 sm:py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Image collage */}
                        <div className="relative h-[400px] sm:h-[500px] lg:h-[580px]">
                            <img
                                src="/images/img_image_4.webp"
                                alt="NEHDO Wellness model in natural light workshop"
                                loading="lazy"
                                decoding="async"
                                className="absolute top-0 left-0 w-[65%] h-[75%] object-cover object-[center_top] rounded-2xl shadow-lg"
                            />
                            <img
                                src="/images/img_image_5.webp"
                                alt="Organic cotton handbag and studio details"
                                loading="lazy"
                                decoding="async"
                                className="absolute bottom-0 right-0 w-[55%] h-[60%] object-cover object-center rounded-2xl shadow-lg border-4 border-background-tertiary"
                            />
                            <div className="absolute bottom-[28%] left-[10%] bg-primary-brand text-white rounded-xl px-4 py-3 shadow-xl">
                                <p className="text-xs font-semibold uppercase tracking-widest text-primary-accent">Est.</p>
                                <p className="text-3xl font-bold">2016</p>
                            </div>
                        </div>

                        {/* Text content */}
                        <div className="flex flex-col gap-6">
                            <span className="text-primary-accent text-xs font-semibold uppercase tracking-[4px]">Brand Story</span>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary leading-tight">
                                Fashion that starts with the farmer, not the factory.
                            </h2>
                            <p className="text-text-opacity-60 text-base sm:text-lg leading-relaxed">
                                NEHDO Wellness was founded in Surat in 2016 by Sidhesh kathrotiya, a textile engineer who grew up watching his grandfather weave khadi. Frustrated by fast fashion's toll on artisan livelihoods and the environment, Arjun set out to build a brand that honoured both.
                            </p>
                            <p className="text-text-opacity-60 text-base sm:text-lg leading-relaxed">
                                Today, we work with over 80 independent weavers, tailors, and dyers across India — paying fair wages, using traceable materials, and designing clothes built to outlast trends.
                            </p>

                            {/* Mission statement */}
                            <div className="bg-background-secondary rounded-2xl p-6 border-l-4 border-primary-accent">
                                <p className="text-xs font-semibold uppercase tracking-widest text-primary-accent mb-2">Our Mission</p>
                                <p className="text-text-primary text-lg sm:text-xl font-semibold leading-snug">
                                    "To make conscious fashion the default choice — beautiful, affordable, and kind to the people and planet behind every thread."
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Timeline ── */}
                <section className="w-full bg-background-secondary py-16 sm:py-20 lg:py-28">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="text-center mb-12 sm:mb-16">
                            <span className="text-primary-accent text-sm sm:text-base lg:text-lg font-bold uppercase tracking-[5px]">A Decade of Growth</span>
                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mt-3">
                                Milestones that shaped us
                            </h2>
                        </div>

                        {/* Year selector */}
                        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-12">
                            {timeline.map((event) => (
                                <button
                                    key={event.year}
                                    onClick={() => setActiveYear(event.year)}
                                    className={`px-5 sm:px-7 py-2.5 sm:py-3 rounded-full text-base sm:text-lg font-semibold transition-all duration-200 ${activeYear === event.year
                                        ? 'bg-primary-brand text-white shadow-lg scale-105'
                                        : 'bg-white text-text-primary border border-border-light hover:border-primary-brand hover:text-primary-brand'
                                        }`}
                                >
                                    {event.year}
                                </button>
                            ))}
                        </div>

                        {/* Active event display - Larger Main Div */}
                        <div className="max-w-5xl mx-auto">
                            <div className="bg-white rounded-3xl p-8 sm:p-12 lg:p-16 shadow-md border border-border-light relative overflow-hidden">
                                <div
                                    className="absolute top-0 right-0 text-[140px] sm:text-[200px] font-black leading-none select-none pointer-events-none"
                                    style={{ color: '#7e420f0d' }}
                                >
                                    {activeEvent.year}
                                </div>
                                <div className="relative z-10">
                                    <span className="inline-block bg-primary-brand text-white text-xs sm:text-sm font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
                                        {activeEvent.year}
                                    </span>
                                    <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-5">
                                        {activeEvent.title}
                                    </h3>
                                    <p className="text-text-opacity-60 text-lg sm:text-xl lg:text-2xl leading-relaxed">
                                        {activeEvent.description}
                                    </p>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="flex items-center gap-2 mt-8">
                                {timeline.map((event, i) => (
                                    <div
                                        key={event.year}
                                        className={`h-2 rounded-full flex-1 transition-all duration-300 ${timeline.findIndex(e => e.year === activeYear) >= i
                                            ? 'bg-primary-brand' : 'bg-border-light'
                                            }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Team ── */}
                <section className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] py-16 sm:py-20 lg:py-24">
                    <div className="flex flex-col gap-2 mb-12 text-left">
                        <span className="text-primary-accent text-xs font-semibold uppercase tracking-[4px]">The People</span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mt-1">
                            Faces behind the fabric
                        </h2>
                        <p className="text-text-opacity-60 text-base sm:text-lg max-w-2xl mt-1">
                            A small team with a big conviction — that fashion can be a force for good.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                        {teamMembers.map((member, index) => (
                            <div
                                key={member.name}
                                className={`group relative rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-lg transition-all duration-300 ${index === 0 ? 'sm:col-span-2 lg:col-span-1' : ''
                                    }`}
                            >
                                <div className="relative overflow-hidden h-[280px] sm:h-[320px]">
                                    <img
                                        src={member.image}
                                        alt={member.alt}
                                        className="w-full h-full object-cover object-[center_top] transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <p className="text-white font-bold text-lg leading-tight">{member.name}</p>
                                        <p className="text-primary-accent text-xs font-semibold uppercase tracking-wider mt-0.5">{member.role}</p>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <p className="text-text-opacity-60 text-sm leading-relaxed italic">"{member.quote}"</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Sustainability ── */}
                <section className="relative w-full overflow-hidden bg-[#1f1915] py-16 sm:py-20 lg:py-24">
                    <div
                        className="absolute inset-0 opacity-40"
                        style={{
                            backgroundImage: `url('/images/img_rectangle_7.webp')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />

                    <div className="relative w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="text-center mb-12">
                            <span className="text-primary-accent text-xs font-bold uppercase tracking-[4px]">Sustainability</span>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3">
                                Our commitment to the planet
                            </h2>
                            <p className="text-white/80 text-base sm:text-lg mt-3 max-w-2xl mx-auto leading-relaxed">
                                Sustainability isn't a marketing line for us — it's the operating system our entire business runs on.
                            </p>
                        </div>

                        {/* Bento grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                            {sustainabilityPillars.map((pillar, index) => (
                                <div
                                    key={pillar.title}
                                    className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-7 hover:bg-white/20 transition-all duration-300 shadow-lg ${index === 0 ? 'sm:col-span-2 lg:col-span-1' : ''
                                        }`}
                                >
                                    <span className="text-3xl">{pillar.icon}</span>
                                    <p className="text-4xl sm:text-5xl font-black text-white mt-4 mb-1">{pillar.stat}</p>
                                    <p className="text-primary-accent text-sm font-bold uppercase tracking-wider mb-2">{pillar.title}</p>
                                    <p className="text-white/80 text-sm leading-relaxed">{pillar.description}</p>
                                </div>
                            ))}
                        </div>

                        {/* CTA */}
                        <div className="mt-12 text-center">
                            <p className="text-white/85 text-base mb-5 font-medium">
                                Every purchase you make directly funds our sustainability initiatives.
                            </p>
                            <Link
                                href="/shop"
                                ariaLabel="Shop sustainably"
                                className="inline-flex items-center gap-2 bg-primary-brand text-white font-semibold px-8 py-3.5 rounded-full hover:bg-primary-brand-dark transition-all duration-200 text-sm sm:text-base shadow-lg"
                            >
                                Shop Consciously →
                            </Link>
                        </div>
                    </div>
                </section>

            </main>
            <Footer />
        </>
    );
}
